/**
 * SelfHealingNotFound — the router's not-found page, with one job before it
 * dares say "not found": make sure it isn't a STALE CACHED SHELL looking at a
 * link minted by a newer build.
 *
 * The live case (2026-07-20): a client opened karr.pro/r/<slug> from their
 * approval email. Short links shipped the day before; the client's installed
 * PWA still held the older app shell, whose router had no /r route — so the
 * app itself rendered "not found" without one request leaving the device, and
 * the packet never even flipped to "viewed". The link was perfect; the shell
 * was old.
 *
 * So: an unknown URL first spends the bounded chunk-reload budget (2/min,
 * SW-update-first — see chunkReload.ts) to pick up the fresh build. Only when
 * the budget is spent — i.e. the URL is genuinely wrong on the NEWEST build —
 * does the calm dead end render. Clients holding real links never see it.
 *
 * @since 1.0.0
 */
import { useState } from 'react';
import { Loader2, Compass } from 'lucide-react';
import { reloadForFreshChunks } from '@/shared/utils/chunkReload';

export function SelfHealingNotFound() {
    // Decided once at first render: true → a reload is already on its way.
    const [healing] = useState(() => reloadForFreshChunks());

    if (healing) {
        return (
            <div className="min-h-screen bg-stone-50 dark:bg-stone-950 flex items-center justify-center px-4">
                <p className="text-sm text-stone-500 flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin" /> One moment — getting the latest version…
                </p>
            </div>
        );
    }
    return (
        <div className="min-h-screen bg-stone-50 dark:bg-stone-950 flex items-center justify-center px-4">
            <div className="max-w-sm text-center">
                <Compass size={28} className="mx-auto text-stone-400" />
                <h1 className="text-base font-semibold text-stone-800 dark:text-stone-100 mt-3">This page doesn’t exist</h1>
                <p className="text-xs text-stone-500 dark:text-stone-400 mt-1.5 leading-relaxed">
                    The address may have been typed with a character missing, or the link is older
                    than the page it pointed to. If someone sent it to you, ask them for a fresh one.
                </p>
                <a href="/" className="inline-block mt-3 text-xs font-medium text-teal-700 dark:text-teal-400 underline">
                    Go to the home screen
                </a>
            </div>
        </div>
    );
}
